<script setup>
// Editor de um lembrete automático de serviço. Cada lembrete tem SUA PRÓPRIA caixa
// de envio (dá pra ter lembretes por caixas diferentes). Se a caixa for WhatsApp
// Cloud oficial, o conteúdo pode ser um template aprovado (dropdown).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  reminder: { type: Object, required: true },
  inboxes: { type: Array, default: () => [] }, // caixas WhatsApp disponíveis
  templatesByInbox: { type: Object, default: () => ({}) }, // cache templates por caixa
});
const emit = defineEmits(['remove', 'load-templates']);
const { t } = useI18n();
const r = props.reminder;

const selectedInbox = computed(() =>
  props.inboxes.find(i => i.id === r.inbox_id)
);
const isOfficial = computed(
  () => (selectedInbox.value?.provider || '') === 'whatsapp_cloud'
);
const templates = computed(() => props.templatesByInbox[r.inbox_id] || []);

const useTemplate = ref(!!props.reminder.wa_template_name);
const onInboxChange = () => {
  // caixa não-oficial não usa template
  if (!isOfficial.value) {
    useTemplate.value = false;
    r.wa_template_name = '';
    r.wa_template_lang = '';
  } else {
    emit('load-templates', r.inbox_id);
  }
};
const onMode = v => {
  useTemplate.value = v;
  if (!v) {
    r.wa_template_name = '';
    r.wa_template_lang = '';
  }
};
// valor do select = "name|lang" (templates podem repetir nome em idiomas diferentes)
const templateKey = computed({
  get: () =>
    r.wa_template_name ? `${r.wa_template_name}|${r.wa_template_lang || ''}` : '',
  set: val => {
    const [name, lang] = (val || '').split('|');
    r.wa_template_name = name || '';
    r.wa_template_lang = lang || '';
  },
});
</script>

<template>
  <div class="p-3 border rounded-lg border-n-weak bg-n-alpha-1">
    <div class="flex items-center gap-2 mb-2">
      <input
        v-model="r.label"
        class="flex-1 w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.services.reminders.labelPlaceholder')"
      />
      <label class="flex items-center gap-1 text-xs"
        ><input v-model="r.is_active" type="checkbox" />
        {{ t('ATENDIMENTOS.services.reminders.active') }}</label
      >
      <button
        class="text-xs text-n-ruby-11 hover:underline"
        @click="emit('remove')"
      >
        {{ t('ATENDIMENTOS.services.reminders.removeShort') }}
      </button>
    </div>

    <!-- timing -->
    <div class="flex flex-wrap items-center gap-1 mb-2 text-xs text-n-slate-11">
      <span>{{ t('ATENDIMENTOS.services.reminders.send') }}</span>
      <input
        v-model.number="r.days_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.days') }}</span>
      <input
        v-model.number="r.hours_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.hours') }}</span>
      <input
        v-model.number="r.minutes_before"
        type="number"
        min="0"
        class="reset-base px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 w-[52px]"
      /><span>{{ t('ATENDIMENTOS.services.reminders.minutes') }}</span>
      <div class="relative flex-1 min-w-[140px]">
        <select
          v-model="r.timing"
          class="w-full appearance-none !bg-none pl-2 pr-8 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="before">
            {{ t('ATENDIMENTOS.services.reminders.beforeConsult') }}
          </option>
          <option value="after">
            {{ t('ATENDIMENTOS.services.reminders.afterConsult') }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
        />
      </div>
    </div>

    <!-- caixa de envio (por lembrete) -->
    <div class="relative mb-2">
      <select
        v-model="r.inbox_id"
        class="w-full appearance-none !bg-none pl-2 pr-8 py-2 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        @change="onInboxChange"
      >
        <option :value="null">
          {{ t('ATENDIMENTOS.services.reminders.selectInbox') }}
        </option>
        <option v-for="i in inboxes" :key="i.id" :value="i.id">
          {{ i.name
          }}{{
            i.provider === 'whatsapp_cloud'
              ? ` ${t('ATENDIMENTOS.services.reminders.officialAPI')}`
              : ''
          }}
        </option>
      </select>
      <span
        class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
      />
    </div>

    <!-- modo: mensagem livre / template (template só em caixa oficial) -->
    <div class="flex gap-1 mb-2">
      <button
        class="px-3 py-1 text-xs rounded-md text-n-slate-10"
        :class="!useTemplate ? 'bg-n-background text-n-slate-12 shadow-sm' : ''"
        @click="onMode(false)"
      >
        {{ t('ATENDIMENTOS.services.reminders.freeMessage') }}
      </button>
      <button
        v-if="isOfficial"
        class="px-3 py-1 text-xs rounded-md text-n-slate-10"
        :class="useTemplate ? 'bg-n-background text-n-slate-12 shadow-sm' : ''"
        @click="onMode(true)"
      >
        {{ t('ATENDIMENTOS.services.reminders.waTemplate') }}
      </button>
    </div>

    <template v-if="!useTemplate || !isOfficial">
      <textarea
        v-model="r.message"
        rows="2"
        class="w-full px-2 py-1 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        :placeholder="t('ATENDIMENTOS.services.reminders.messagePlaceholder')"
      />
    </template>
    <template v-else>
      <div v-if="!templates.length" class="text-[0.6875rem] text-n-amber-11">
        {{ t('ATENDIMENTOS.services.reminders.noApprovedTemplates') }}
      </div>
      <div v-else class="relative">
        <select
          v-model="templateKey"
          class="w-full appearance-none !bg-none pl-2 pr-8 py-2 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
        >
          <option value="">
            {{ t('ATENDIMENTOS.services.reminders.selectTemplate') }}
          </option>
          <option
            v-for="tpl in templates"
            :key="`${tpl.name}|${tpl.language}`"
            :value="`${tpl.name}|${tpl.language}`"
          >
            {{ tpl.name }} ({{ tpl.language }})
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-n-slate-10"
        />
      </div>
    </template>
  </div>
</template>
